import { useEffect, useRef, useState, type ReactNode } from 'react';
import { breakpoint } from '@circles/tokens';
import { AppShell } from '../shell/AppShell';
import { SizeContext, type SizeClass } from '../shell/adaptive';
import { cx } from '../components/ui';
import { setState, useStore, type Layout } from '../state/store';
import { accentStyle, useActiveTheme } from '../state/theme';

/**
 * Screen sizes in points. The iPhone Duo is not released yet; its sizes are estimates from the
 * stated diagonals (5.4" outer like iPhone mini, 7.6" inner at about 6:5) and need checking on
 * the real device (task 2.5).
 */
export const SCREENS = {
  phone: { w: 393, h: 852, label: 'Phone (6.1")' },
  duoOuter: { w: 375, h: 812, label: 'iPhone Duo, outer 5.4"' },
  duoInner: { w: 744, h: 892, label: 'iPhone Duo, inner 7.6"' },
  desktop: { w: 1200, h: 760, label: 'Desktop window' },
} as const;

function useSize(width: number, platform: SizeClass['platform']): SizeClass {
  return { width, wide: width >= breakpoint.twoPane, platform };
}

/** The app rendered into a region of a given width, with its own size class. */
function AppRegion({
  width,
  platform,
  children,
}: {
  width: number;
  platform: SizeClass['platform'];
  children?: ReactNode;
}) {
  const size = useSize(width, platform);
  const theme = useActiveTheme();
  return (
    <SizeContext.Provider value={size}>
      <div
        className={cx('app-region', `platform-${platform}`, `theme-${theme.id}`)}
        style={accentStyle(theme.accent)}
        data-mode={theme.mode}
      >
        {children ?? <AppShell />}
      </div>
    </SizeContext.Provider>
  );
}

function StatusBar({ wide }: { wide?: boolean }) {
  return (
    <div className={cx('status-bar', wide && 'is-wide')} aria-hidden>
      <span className="status-time">9:41</span>
      <span className="status-icons">
        <svg width="18" height="12" viewBox="0 0 18 12">
          <rect x="0" y="8" width="3" height="4" rx="1" />
          <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
          <rect x="10" y="3" width="3" height="9" rx="1" />
          <rect x="15" y="0" width="3" height="12" rx="1" />
        </svg>
        <svg width="16" height="12" viewBox="0 0 16 12">
          <path d="M8 2.2c2.4 0 4.6.9 6.2 2.5l1.3-1.3C13.5 1.4 10.9.3 8 .3S2.5 1.4.5 3.4l1.3 1.3C3.4 3.1 5.6 2.2 8 2.2zm0 3.6c1.4 0 2.7.5 3.6 1.4l1.3-1.3C11.6 4.6 9.9 3.9 8 3.9s-3.6.7-4.9 2l1.3 1.3c.9-.9 2.2-1.4 3.6-1.4zm0 3.4c-.6 0-1.1.2-1.5.6L8 11.3l1.5-1.5c-.4-.4-.9-.6-1.5-.6z" />
        </svg>
        <svg width="27" height="13" viewBox="0 0 27 13">
          <rect
            x=".5"
            y=".5"
            width="23"
            height="12"
            rx="3.5"
            fill="none"
            stroke="currentColor"
            opacity=".4"
          />
          <rect x="2" y="2" width="17" height="9" rx="2" />
          <path d="M25 4.5v4c.8-.3 1.4-1.1 1.4-2s-.6-1.7-1.4-2z" opacity=".4" />
        </svg>
      </span>
    </div>
  );
}

function Phone({ w, h, island = 'island' }: { w: number; h: number; island?: 'island' | 'notch' }) {
  return (
    <div className="device device-phone" style={{ width: w + 24, height: h + 24 }}>
      <div
        className="device-screen"
        style={{ width: w, height: h, ['--safe-top' as string]: '50px' }}
      >
        <StatusBar />
        <span className={cx('device-island', island === 'notch' && 'is-notch')} />
        <div className="device-app" style={{ top: 50, bottom: 0 }}>
          <AppRegion width={w} platform="ios" />
        </div>
        <span className="home-indicator" />
      </div>
    </div>
  );
}

function DuoInner({
  landscape,
  split,
}: {
  landscape: boolean;
  split?: 'half' | 'twoThirds' | 'third';
}) {
  const w = landscape ? SCREENS.duoInner.h : SCREENS.duoInner.w;
  const h = landscape ? SCREENS.duoInner.w : SCREENS.duoInner.h;
  const ratio = split === 'twoThirds' ? 2 / 3 : split === 'third' ? 1 / 3 : 0.5;
  const divider = 10;
  const circlesW = split ? Math.round((w - divider) * ratio) : w;
  return (
    <div
      className={cx('device device-fold', landscape && 'is-landscape')}
      style={{ width: w + 22, height: h + 22 }}
    >
      <div
        className="device-screen"
        style={{ width: w, height: h, ['--safe-top' as string]: '34px' }}
      >
        <StatusBar wide />
        <span className="device-camera" />
        <div className="device-app" style={{ top: 34, bottom: 0 }}>
          {split ? (
            <div className="split-view">
              <div className="split-pane" style={{ width: circlesW }}>
                <AppRegion width={circlesW} platform="ios" />
              </div>
              <div className="split-divider" style={{ width: divider }}>
                <span />
              </div>
              <div className="split-pane other-app" style={{ width: w - circlesW - divider }}>
                <OtherApp />
              </div>
            </div>
          ) : (
            <AppRegion width={w} platform="ios" />
          )}
        </div>
        <span className={cx('fold-crease', landscape && 'is-landscape')} />
        <span className="home-indicator" />
      </div>
    </div>
  );
}

/** A stand-in for the second app in Split View. */
function OtherApp() {
  const items = [
    ['09:00', 'Depot walk-through'],
    ['11:30', 'Elbe terminal call'],
    ['14:00', 'Lübeck delivery window'],
    ['16:30', 'Rota planning'],
  ];
  return (
    <div className="other-app-inner">
      <div className="other-app-title">Calendar</div>
      <div className="other-app-day">Tuesday, 17 November</div>
      {items.map(([t, label]) => (
        <div key={t} className="other-app-event">
          <span>{t}</span>
          {label}
        </div>
      ))}
      <p className="other-app-note">Another app in Split View</p>
    </div>
  );
}

function Desktop({ os }: { os: 'macos' | 'windows' }) {
  const { w, h } = SCREENS.desktop;
  const titleH = os === 'macos' ? 38 : 34;
  return (
    <div className={cx('desktop-window', `os-${os}`)} style={{ width: w, height: h }}>
      <div className="window-titlebar" style={{ height: titleH }}>
        {os === 'macos' ? (
          <>
            <span className="traffic-lights">
              <i />
              <i />
              <i />
            </span>
            <span className="window-title">Circles</span>
          </>
        ) : (
          <>
            <span className="window-title win">Circles</span>
            <span className="win-controls">
              <svg viewBox="0 0 10 10" width="10" height="10">
                <path d="M0 5h10" stroke="currentColor" />
              </svg>
              <svg viewBox="0 0 10 10" width="10" height="10">
                <rect x=".5" y=".5" width="9" height="9" fill="none" stroke="currentColor" />
              </svg>
              <svg viewBox="0 0 10 10" width="10" height="10">
                <path d="M0 0l10 10M10 0L0 10" stroke="currentColor" />
              </svg>
            </span>
          </>
        )}
      </div>
      <div className="window-body" style={{ height: h - titleH }}>
        <AppRegion width={w} platform={os} />
      </div>
    </div>
  );
}

export function frameSize(layout: Layout, landscape: boolean): { w: number; h: number } {
  switch (layout) {
    case 'phone':
      return { w: SCREENS.phone.w + 24, h: SCREENS.phone.h + 24 };
    case 'duoOuter':
      return { w: SCREENS.duoOuter.w + 24, h: SCREENS.duoOuter.h + 24 };
    case 'duoInner':
    case 'duoSplit': {
      const l = layout === 'duoInner' ? landscape : true;
      return {
        w: (l ? SCREENS.duoInner.h : SCREENS.duoInner.w) + 22,
        h: (l ? SCREENS.duoInner.w : SCREENS.duoInner.h) + 22,
      };
    }
    default:
      return { w: SCREENS.desktop.w, h: SCREENS.desktop.h };
  }
}

export function Stage() {
  const layout = useStore((s) => s.layout);
  const landscape = useStore((s) => s.landscape);
  const split = useStore((s) => s.split);
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const size = frameSize(layout, landscape);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const pad = 48;
      setScale(Math.min(1, (el.clientWidth - pad) / size.w, (el.clientHeight - pad) / size.h));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [size.w, size.h]);

  return (
    <main className={cx('stage', layout.startsWith('desktop') && 'is-desktop')} ref={ref}>
      <div
        className="stage-frame"
        style={{ width: size.w, height: size.h, transform: `scale(${scale})` }}
      >
        {layout === 'phone' && <Phone w={SCREENS.phone.w} h={SCREENS.phone.h} />}
        {layout === 'duoOuter' && (
          <Phone w={SCREENS.duoOuter.w} h={SCREENS.duoOuter.h} island="notch" />
        )}
        {layout === 'duoInner' && <DuoInner landscape={landscape} />}
        {layout === 'duoSplit' && <DuoInner landscape split={split} />}
        {layout === 'desktopMac' && <Desktop os="macos" />}
        {layout === 'desktopWin' && <Desktop os="windows" />}
      </div>
      {layout === 'duoInner' && (
        <button
          type="button"
          className="stage-rotate"
          onClick={() => setState({ landscape: !landscape })}
        >
          Rotate
        </button>
      )}
    </main>
  );
}
